import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Send, FileText, Loader2, Globe, FolderOpen, Sparkles, Zap, AlertTriangle, AlertCircle, ArrowRight, TrendingUp, Languages, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage, LANGUAGES, translations } from '@/contexts/LanguageContext.jsx';

export const ChatPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [searchMode, setSearchMode] = useState('documents'); // 'documents', 'web', or 'hybrid' - default to documents
  const [usage, setUsage] = useState(null);
  const [responseLanguage, setResponseLanguage] = useState('auto'); // 'auto', 'en', 'ms', 'zh', 'ta'
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const messagesEndRef = useRef(null);
  const lastAnswerRef = useRef(null);
  const lastQuestionRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const langDropdownRef = useRef(null);
  const { language } = useLanguage();
  const t = translations[language] || translations.en;

  useEffect(() => {
    loadHistory();
    loadUsage();
  }, []);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUsage = async () => {
    try {
      const response = await axios.get(`${API}/usage/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsage(response.data);
    } catch (error) {
      console.error('Failed to load usage:', error);
    }
  };

  // Scroll to the latest user question when it's added (before answer arrives)
  useEffect(() => {
    if (loading && lastQuestionRef.current) {
      // When loading starts (question submitted), scroll to show the question
      // Use 'center' to ensure the question is prominently visible
      setTimeout(() => {
        lastQuestionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
  }, [loading]);

  // Scroll to top of last answer when AI response arrives
  useEffect(() => {
    // Only scroll when we have messages and are not loading (answer just arrived)
    const lastMessage = messages[messages.length - 1];
    if (!loading && lastMessage?.type === 'ai' && lastAnswerRef.current) {
      // Delay to ensure the answer is fully rendered, then scroll to start of answer
      setTimeout(() => {
        lastAnswerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [messages, loading]);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  // Check if user is blocked (either company monthly or user daily limit)
  const isBlocked = usage && (usage.alert_level === 'limit_reached' || usage.user_daily_limit_reached);
  const blockReason = usage?.alert_level === 'limit_reached' ? 'company_monthly' : (usage?.user_daily_limit_reached ? 'user_daily' : null);

  const handleAsk = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    // Check if user has reached any query limit before sending
    if (isBlocked) {
      if (blockReason === 'user_daily') {
        toast.error('Daily query limit reached! Please try again tomorrow.');
      } else {
        toast.error('Monthly query limit reached! Please upgrade your plan to continue.');
      }
      return;
    }

    const userMessage = { type: 'user', text: question };
    setMessages(prev => [...prev, userMessage]);
    setQuestion('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/ask`,
        { question, search_mode: searchMode, response_language: responseLanguage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        type: 'ai',
        text: response.data.answer,
        sources: response.data.sources
      };
      setMessages(prev => [...prev, aiMessage]);
      loadHistory();
      
      // Update usage stats after successful query
      if (response.data.usage) {
        setUsage(response.data.usage);
      } else {
        // Fallback: reload usage from API
        loadUsage();
      }
    } catch (error) {
      // Handle specific limit reached errors from backend
      if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        if (detail?.block_reason === 'user_daily') {
          toast.error('Daily query limit reached! Please try again tomorrow.');
        } else {
          toast.error('Monthly query limit reached! Please upgrade your plan.');
        }
        if (detail?.usage) {
          setUsage(detail.usage);
        }
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to get answer');
      }
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex bg-slate-950">
      {/* Main chat area */}
      <div className="flex-1 flex flex-col p-8">
        {/* User Daily Limit Alert Banner */}
        {usage && usage.user_daily_limit_reached && (
          <div className="mb-4 p-4 rounded-lg border bg-orange-500/10 border-orange-500/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="font-medium text-orange-300">
                    Daily query limit reached!
                  </p>
                  <p className="text-sm text-slate-400">
                    You've used {usage.user_daily_used}/{usage.user_daily_limit} queries today. Resets at midnight UTC.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Monthly Usage Alert Banner */}
        {usage && !usage.user_daily_limit_reached && usage.alert_level !== 'normal' && (
          <div className={`mb-4 p-4 rounded-lg border ${
            usage.alert_level === 'limit_reached' 
              ? 'bg-red-500/10 border-red-500/30' 
              : usage.alert_level === 'critical' 
                ? 'bg-red-500/10 border-red-500/30' 
                : 'bg-amber-500/10 border-amber-500/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {usage.alert_level === 'limit_reached' ? (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                ) : (
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                )}
                <div>
                  <p className={`font-medium ${
                    usage.alert_level === 'limit_reached' || usage.alert_level === 'critical' 
                      ? 'text-red-300' : 'text-amber-300'
                  }`}>
                    {usage.alert_level === 'limit_reached' 
                      ? 'Company query limit reached!' 
                      : usage.alert_level === 'critical' 
                        ? `Only ${usage.queries_remaining} company queries left!` 
                        : `${usage.queries_remaining} company queries remaining this month`}
                  </p>
                  <p className="text-sm text-slate-400">
                    {usage.queries_used} / {usage.queries_limit} company queries used ({usage.usage_percent}%)
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => navigate('/suite/docs/billing')}
                className={`${
                  usage.alert_level === 'limit_reached' || usage.alert_level === 'critical' 
                    ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
                } text-white`}
              >
                Upgrade Plan <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">AI Assistant</h1>
              <p className="text-base text-slate-400 mt-2">{t.askInAnyLanguage}</p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Response Language Selector */}
              <div className="relative" ref={langDropdownRef}>
                <button
                  onClick={() => setShowLangDropdown(!showLangDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-900 rounded-lg border border-slate-800 text-slate-300 hover:text-white hover:border-slate-700 transition-colors"
                  data-testid="response-language-selector"
                >
                  <Languages size={16} className="text-emerald-400" />
                  <span className="text-sm">
                    {responseLanguage === 'auto' ? 'Auto' : LANGUAGES[responseLanguage]?.nativeName || responseLanguage.toUpperCase()}
                  </span>
                  <ChevronDown size={14} className={`transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showLangDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
                    <div className="p-2 border-b border-slate-800">
                      <p className="text-xs text-slate-500 px-2">{t.preferredLanguage}</p>
                    </div>
                    <div className="p-1">
                      <button
                        onClick={() => { setResponseLanguage('auto'); setShowLangDropdown(false); }}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                          responseLanguage === 'auto' ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                        }`}
                        data-testid="lang-auto"
                      >
                        <div className="flex items-center gap-2">
                          <span>🌐</span>
                          <span>Auto-detect</span>
                        </div>
                        {responseLanguage === 'auto' && <Check size={14} />}
                      </button>
                      {Object.values(LANGUAGES).map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => { setResponseLanguage(lang.code); setShowLangDropdown(false); }}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
                            responseLanguage === lang.code ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                          }`}
                          data-testid={`lang-${lang.code}`}
                        >
                          <div className="flex items-center gap-2">
                            <span>{lang.flag}</span>
                            <span>{lang.nativeName}</span>
                          </div>
                          {responseLanguage === lang.code && <Check size={14} />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Search Mode Toggle */}
              <div className="flex gap-2 bg-slate-900 rounded-lg p-1 border border-slate-800">
                <Button
                  variant={searchMode === 'documents' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchMode('documents')}
                  data-testid="documents-only-mode"
                  className={searchMode === 'documents' ? 'bg-indigo-600 hover:bg-indigo-700' : 'text-slate-400 hover:text-white hover:bg-slate-800'}
                  title="Search company documents only"
                >
                  <FolderOpen size={16} className="mr-2" />
                  Documents
                </Button>
                <Button
                  variant={searchMode === 'hybrid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchMode('hybrid')}
                  data-testid="hybrid-mode"
                  className={searchMode === 'hybrid' ? 'bg-indigo-600 hover:bg-indigo-700' : 'text-slate-400 hover:text-white hover:bg-slate-800'}
                  title="Search documents + web"
                >
                  <Sparkles size={16} className="mr-2" />
                  Hybrid
                </Button>
                <Button
                  variant={searchMode === 'web' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSearchMode('web')}
                  data-testid="web-only-mode"
                  className={searchMode === 'web' ? 'bg-indigo-600 hover:bg-indigo-700' : 'text-slate-400 hover:text-white hover:bg-slate-800'}
                  title="Search web only"
                >
                  <Globe size={16} className="mr-2" />
                  Web
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Chat messages */}
        <Card className="flex-1 mb-6 flex flex-col overflow-hidden bg-slate-900/50 border-slate-800">
          <ScrollArea className="flex-1 p-6">
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center" data-testid="chat-empty-state">
                <div className="w-20 h-20 bg-indigo-600/10 rounded-full flex items-center justify-center mb-4">
                  <FileText size={32} className="text-indigo-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Start a conversation</h3>
                <p className="text-slate-400 max-w-md">Ask any question about your company documents and get instant, accurate answers.</p>
              </div>
            ) : (
              <div className="space-y-6 max-w-3xl mx-auto">
                {messages.map((msg, idx) => {
                  // Check if this is the last user message (for scrolling when question is asked)
                  const isLastUserMessage = msg.type === 'user' && 
                    (idx === messages.length - 1 || (idx === messages.length - 2 && messages[messages.length - 1]?.type === 'ai'));
                  
                  // Check if this is the last AI message (for scrolling when answer arrives)
                  const isLastAiMessage = msg.type === 'ai' && idx === messages.length - 1;
                  
                  return (
                  <div 
                    key={idx} 
                    className="animate-in fade-in slide-in-from-bottom-4 duration-500"
                    ref={isLastAiMessage ? lastAnswerRef : (isLastUserMessage ? lastQuestionRef : null)}
                  >
                    {msg.type === 'user' ? (
                      <div className="flex justify-end" data-testid="user-message">
                        <div className="bg-indigo-600 rounded-lg px-6 py-4 max-w-2xl">
                          <p className="text-white">{msg.text}</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-start" data-testid="ai-message">
                        <div className="bg-slate-800 border-l-4 border-indigo-500 rounded-lg px-6 py-4 max-w-2xl">
                          <div className="text-white leading-relaxed mb-4 prose prose-invert prose-sm max-w-none">
                            <ReactMarkdown 
                              remarkPlugins={[remarkGfm]}
                              components={{
                                // Style markdown elements
                                p: ({node, ...props}) => <p className="mb-3 last:mb-0" {...props} />,
                                ul: ({node, ...props}) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
                                ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
                                li: ({node, ...props}) => <li className="ml-2" {...props} />,
                                strong: ({node, ...props}) => <strong className="font-bold text-indigo-300" {...props} />,
                                em: ({node, ...props}) => <em className="italic text-slate-300" {...props} />,
                                h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2 mt-4" {...props} />,
                                h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2 mt-3" {...props} />,
                                h3: ({node, ...props}) => <h3 className="text-base font-bold mb-2 mt-2" {...props} />,
                                code: ({node, inline, ...props}) => 
                                  inline ? 
                                    <code className="bg-slate-700 px-1.5 py-0.5 rounded text-indigo-300 text-sm" {...props} /> :
                                    <code className="block bg-slate-700 p-2 rounded text-indigo-300 text-sm my-2" {...props} />,
                                blockquote: ({node, ...props}) => <blockquote className="border-l-2 border-indigo-500 pl-3 italic text-slate-400 my-2" {...props} />,
                              }}
                            >
                              {msg.text}
                            </ReactMarkdown>
                          </div>
                          {msg.sources && msg.sources.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-slate-700">
                              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Sources</p>
                              <div className="space-y-2">
                                {msg.sources.map((source, sidx) => (
                                  <div key={sidx} className="text-sm text-slate-300 flex items-start gap-2" data-testid="source-item">
                                    {source.type === 'web' ? (
                                      <Globe size={14} className="mt-0.5 text-emerald-400 flex-shrink-0" />
                                    ) : (
                                      <FileText size={14} className="mt-0.5 text-indigo-400 flex-shrink-0" />
                                    )}
                                    <div>
                                      <span className="font-medium">{source.title}</span>
                                      {source.type === 'web' && <span className="ml-2 text-xs text-emerald-400">(Web)</span>}
                                      <p className="text-xs text-slate-400 mt-1">{source.snippet}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                {loading && (
                  <div className="flex justify-start" data-testid="loading-indicator">
                    <div className="bg-slate-800 border-l-4 border-indigo-500 rounded-lg px-6 py-4">
                      <Loader2 className="animate-spin text-indigo-400" size={20} />
                    </div>
                  </div>
                )}
                {/* Dummy div for auto-scroll */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>
        </Card>

        {/* Input area */}
        <div className="space-y-2">
          {/* Usage mini indicator - shows both company and user daily limits */}
          {usage && !usage.message && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4 text-slate-400">
                {/* Company monthly usage */}
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${
                    usage.alert_level === 'limit_reached' ? 'text-red-400' :
                    usage.alert_level === 'critical' ? 'text-red-400' :
                    usage.alert_level === 'warning' ? 'text-amber-400' : 'text-indigo-400'
                  }`} />
                  <span title="Company monthly queries">
                    {usage.queries_used}/{usage.queries_limit} monthly
                  </span>
                </div>
                {/* User daily usage */}
                <div className="flex items-center gap-2">
                  <TrendingUp className={`w-4 h-4 ${
                    usage.user_daily_limit_reached ? 'text-orange-400' : 'text-green-400'
                  }`} />
                  <span title="Your daily queries">
                    {usage.user_daily_used}/{usage.user_daily_limit} today
                  </span>
                </div>
              </div>
              <div className="w-32">
                <Progress 
                  value={usage.usage_percent} 
                  className={`h-1.5 bg-slate-800`}
                />
              </div>
            </div>
          )}
          
          <form onSubmit={handleAsk} className="flex gap-4">
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={isBlocked 
                ? (blockReason === 'user_daily' 
                    ? "Daily limit reached - try again tomorrow..." 
                    : "Company limit reached - upgrade to continue...")
                : "Ask a question about your documents..."}
              disabled={loading || isBlocked}
              data-testid="question-input"
              className={`flex-1 h-12 bg-slate-900 border-slate-800 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-white text-base ${
                isBlocked ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            />
            <Button
              type="submit"
              disabled={loading || !question.trim() || isBlocked}
              data-testid="send-question-button"
              className={`h-12 px-8 text-white font-semibold rounded-md glow-effect ${
                isBlocked
                  ? 'bg-slate-600 cursor-not-allowed' 
                  : 'bg-indigo-600 hover:bg-indigo-700'
              }`}
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
            </Button>
          </form>
        </div>
      </div>

      {/* Recent questions sidebar */}
      <aside className="hidden xl:block w-80 border-l border-slate-800 bg-slate-900 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Questions</h3>
        <ScrollArea className="h-full">
          <div className="space-y-3">
            {history.slice(0, 10).map((item) => (
              <Card
                key={item.id}
                className="p-4 hover:bg-slate-800 transition-colors cursor-pointer bg-slate-950 border-slate-800"
                onClick={() => setQuestion(item.question_text)}
                data-testid="history-item"
              >
                <p className="text-sm text-white font-medium line-clamp-2 mb-2">{item.question_text}</p>
                <p className="text-xs text-slate-400">{new Date(item.created_at).toLocaleDateString()}</p>
              </Card>
            ))}
            {history.length === 0 && (
              <p className="text-sm text-slate-400 text-center py-8">No recent questions</p>
            )}
          </div>
        </ScrollArea>
      </aside>
    </div>
  );
};

export default ChatPage;
